import React, { useMemo } from 'react';
import { useGames } from '@/hooks/useShared';
import { calculateVenueStats } from '@/services/insights';
import { InsightsSubNav } from '@/components/insights/InsightsSubNav';
import { formatCurrency, formatCurrencyShort } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui';
import type { VenueStatEntry } from '@/lib/types/insights';

export function VenueStatsPage() {
  const { games, loading } = useGames();

  const venueStats = useMemo(() => calculateVenueStats(games), [games]);

  if (loading) return <LoadingSpinner message="Loading venue stats..." />;

  return (
    <div className="max-w-[400px] mx-auto">
      <Header title="Venues" subtitle="VENUE STATISTICS" />

      <InsightsSubNav />

      {venueStats.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: 60, fontSize: 13 }}>No archived games yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {venueStats.map(venue => (
            <VenueCard key={venue.venueName} venue={venue} />
          ))}
        </div>
      )}
    </div>
  );
}

function VenueCard({ venue }: { venue: VenueStatEntry }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#2c2f30', marginBottom: 10 }}>{venue.venueName}</h3>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <VenueMiniStat label="Games" value={String(venue.gamesPlayed)} />
        <VenueMiniStat label="Wagered" value={formatCurrencyShort(venue.totalWagered)} />
        <VenueMiniStat label="Avg Players" value={String(venue.avgPlayers)} />
        <VenueMiniStat label="Avg Pot" value={formatCurrencyShort(venue.avgPot)} />
      </div>

      {venue.dominantPlayer && (
        <div style={{ background: '#e8f5f0', borderRadius: 8, padding: '8px 10px', fontSize: 10 }}>
          <span style={{ fontWeight: 700, color: '#006855' }}>Dominant: </span>
          <span style={{ color: '#2c2f30' }}>{venue.dominantPlayer.name}</span>
          <span style={{ color: '#6b7280', marginLeft: 4 }}>
            · {formatCurrency(venue.dominantPlayer.totalPnl)} · {venue.dominantPlayer.gamesPlayed} games
          </span>
        </div>
      )}
    </div>
  );
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ background: '#e8f5f0', border: '1px solid rgba(0,104,85,0.1)', borderRadius: 16, padding: 20, marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: 'rgba(0,104,85,0.7)', marginBottom: 4 }}>{subtitle}</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2c2f30', lineHeight: 1.2 }}>{title}</h1>
    </div>
  );
}

function VenueMiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: 1, background: '#f5f7f8', borderRadius: 8, padding: '6px 4px', textAlign: 'center' }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#2c2f30', marginTop: 1 }}>{value}</div>
    </div>
  );
}
